import { useEffect, useState } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Zap,
  TrendingUp,
  Clock,
  Users,
  ArrowRight,
  UserPlus,
} from 'react-feather';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  CollectionTriggerCondition,
  CollectionTriggerCreate,
  CollectionTriggerType,
  CollectionTriggerWithUser,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierCollectionTriggers,
  createCollectionTrigger,
  updateCollectionTrigger,
  deleteCollectionTrigger,
} from '../../../../redux/services/collectionTriggerService';
import { getSupplierCollectionSequences } from '../../../../redux/services/collectionSequenceService';
import { getSupplierBuyers } from '../../../../redux/services/supplierBuyersService';
import { DillButton, DillToggle } from '../../../general/Dill';
import { CollectionTriggerFormModal } from './CollectionTriggerFormModal';
import { EnrollCustomersModal } from './EnrollCustomersModal';
import { useCollectionBuyerEngagements } from './useCollectionBuyerEngagements';

const BUCKET_LABELS: Record<string, string> = {
  '1to30': '1-30 days',
  '31to60': '31-60 days',
  '61to90': '61-90 days',
  over90: '90+ days',
};

const CONDITION_STYLES: Record<CollectionTriggerType, { icon: typeof TrendingUp; chip: string }> = {
  AGING_BUCKET_THRESHOLD: { icon: TrendingUp, chip: 'bg-blue-50 text-blue-600' },
  DAYS_PAST_DUE: { icon: Clock, chip: 'bg-purple-50 text-purple-600' },
};

const summarizeCondition = (condition: CollectionTriggerCondition): string => {
  if (condition.type === 'AGING_BUCKET_THRESHOLD') {
    return `${
      BUCKET_LABELS[condition.bucket]
    } bucket ≥ $${condition.amountThreshold.toLocaleString()}`;
  }
  return `Any invoice ${condition.days}+ days past due`;
};

interface CollectionTriggersTabProps {
  supplierId: string;
}

export const CollectionTriggersTab = ({ supplierId }: CollectionTriggersTabProps) => {
  const dispatch = useAppDispatch();
  const { triggers, loadingList } = useAppSelector((state) => state.collectionTriggers);
  const { sequences } = useAppSelector((state) => state.collectionSequences);
  const { supplierBuyers } = useAppSelector((state) => state.supplierBuyers);
  const { isBuyerAvailable, refresh: refreshEngagements } =
    useCollectionBuyerEngagements(supplierId);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState<CollectionTriggerWithUser | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addBuyersModalTrigger, setAddBuyersModalTrigger] =
    useState<CollectionTriggerWithUser | null>(null);
  const [isAddingBuyers, setIsAddingBuyers] = useState(false);
  const [addBuyersErrorMessage, setAddBuyersErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (supplierId) {
      dispatch(getSupplierCollectionTriggers({ supplierId }));
      dispatch(getSupplierCollectionSequences({ supplierId }));
    }
  }, [supplierId, dispatch]);

  useEffect(() => {
    if (supplierId && supplierBuyers.length === 0) {
      dispatch(getSupplierBuyers({ supplierId }));
    }
  }, [supplierId, supplierBuyers.length, dispatch]);

  const isLoading = loadingList.includes('getSupplierCollectionTriggers');

  const handleCreate = () => {
    setEditingTrigger(null);
    setErrorMessage(null);
    setIsFormModalOpen(true);
    refreshEngagements();
  };

  const handleEdit = (trigger: CollectionTriggerWithUser) => {
    setEditingTrigger(trigger);
    setErrorMessage(null);
    setIsFormModalOpen(true);
    refreshEngagements();
  };

  const handleClose = () => {
    setIsFormModalOpen(false);
    setEditingTrigger(null);
    setErrorMessage(null);
  };

  const extractErrorMessage = (payload: unknown): string | null => {
    if (payload && typeof payload === 'object' && 'errors' in (payload as any)) {
      const errors = (payload as any).errors;
      if (Array.isArray(errors) && errors[0]?.msg) return errors[0].msg as string;
    }
    return null;
  };

  const handleSubmit = async (data: CollectionTriggerCreate) => {
    if (!supplierId) return;
    setErrorMessage(null);
    const result = editingTrigger
      ? await dispatch(updateCollectionTrigger({ supplierId, triggerId: editingTrigger.id, data }))
      : await dispatch(createCollectionTrigger({ supplierId, data: { ...data, supplierId } }));
    const payload = result.payload as { success?: boolean } | null;
    if (!payload || payload.success === false) {
      setErrorMessage(extractErrorMessage(payload) || 'Failed to save collection trigger.');
      return;
    }
    handleClose();
    dispatch(getSupplierCollectionTriggers({ supplierId }));
    refreshEngagements();
  };

  const handleToggleActive = async (trigger: CollectionTriggerWithUser) => {
    await dispatch(
      updateCollectionTrigger({
        supplierId,
        triggerId: trigger.id,
        data: { isActive: !trigger.isActive },
      })
    );
    dispatch(getSupplierCollectionTriggers({ supplierId }));
  };

  const handleDelete = async (triggerId: string) => {
    await dispatch(deleteCollectionTrigger({ supplierId, triggerId }));
    dispatch(getSupplierCollectionTriggers({ supplierId }));
    refreshEngagements();
  };

  const buyerOptions = supplierBuyers
    .filter((b) => isBuyerAvailable(b.id))
    .map((b) => ({ label: b.name || 'Unnamed customer', value: b.id }));
  const unavailableBuyerCount = supplierBuyers.length - buyerOptions.length;

  const handleOpenAddBuyersModal = (trigger: CollectionTriggerWithUser) => {
    setAddBuyersErrorMessage(null);
    setAddBuyersModalTrigger(trigger);
    refreshEngagements();
  };

  const handleCloseAddBuyersModal = () => {
    setAddBuyersModalTrigger(null);
    setAddBuyersErrorMessage(null);
  };

  const handleAddBuyersSubmit = async (buyerIds: string[]) => {
    if (!addBuyersModalTrigger || buyerIds.length === 0) return;
    setAddBuyersErrorMessage(null);
    setIsAddingBuyers(true);
    const result = await dispatch(
      updateCollectionTrigger({
        supplierId,
        triggerId: addBuyersModalTrigger.id,
        data: {
          targetBuyerIds: [...addBuyersModalTrigger.targetBuyerIds, ...buyerIds],
        },
      })
    );
    setIsAddingBuyers(false);
    const payload = result.payload as { success?: boolean } | null;
    if (!payload || payload.success === false) {
      setAddBuyersErrorMessage(extractErrorMessage(payload) || 'Failed to add customers.');
      return;
    }
    handleCloseAddBuyersModal();
    dispatch(getSupplierCollectionTriggers({ supplierId }));
    refreshEngagements();
  };

  return (
    <div className="w-full h-full pt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <Zap size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 m-0">Collection Triggers</p>
            <p className="text-xs text-gray-400 mt-0.5 m-0">
              Watches specific customers for a condition, then enrolls them into a Collection
              Sequence.
            </p>
          </div>
        </div>
        <DillButton
          label="New trigger"
          icon={<Plus size={14} />}
          onClick={handleCreate}
          height={31}
        />
      </div>

      {isLoading && triggers.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-8">Loading collection triggers…</p>
      )}

      {!isLoading && triggers.length === 0 && (
        <div className="bg-white border border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-300 flex items-center justify-center">
            <Zap size={18} />
          </div>
          <p className="text-sm text-gray-500 m-0 mt-1">No collection triggers yet</p>
          <p className="text-xs text-gray-400 m-0">
            Create one to automatically enroll specific customers into a sequence when a condition
            becomes true.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {triggers.map((trigger) => {
          const { icon: ConditionIcon, chip } = CONDITION_STYLES[trigger.condition.type];

          return (
            <div
              key={trigger.id}
              className="group bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${chip}`}>
                <ConditionIcon size={18} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-gray-900 m-0 truncate">{trigger.name}</p>
                  {!trigger.isActive && (
                    <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 shrink-0">
                      Paused
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 shrink-0">
                    <Users size={10} />
                    {trigger.targetBuyerIds.length} customer
                    {trigger.targetBuyerIds.length === 1 ? '' : 's'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 m-0 flex items-center gap-1 truncate">
                  When {summarizeCondition(trigger.condition)}
                  <ArrowRight size={11} className="text-gray-300 shrink-0" />
                  <span className="font-medium text-gray-700">
                    {trigger.sequence?.name || 'Sequence removed'}
                  </span>
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0 pl-3 border-l border-gray-100">
                <span data-tooltip-id={`trigger-toggle-tooltip-${trigger.id}`}>
                  <DillToggle
                    value={trigger.isActive}
                    onChange={() => handleToggleActive(trigger)}
                  />
                </span>
                <ReactTooltip
                  id={`trigger-toggle-tooltip-${trigger.id}`}
                  place="top"
                  variant="dark"
                  opacity="100%"
                  style={{ borderRadius: '8px', fontSize: '12px', zIndex: 10000 }}>
                  {trigger.isActive
                    ? 'Active — new customers can be watched and enrolled when the condition is met'
                    : 'Paused — no customers will be watched or enrolled until reactivated'}
                </ReactTooltip>

                <DillButton
                  variant="secondary"
                  label="Add customers"
                  icon={<UserPlus size={12} />}
                  height={28}
                  onClick={() => handleOpenAddBuyersModal(trigger)}
                />
                <DillButton
                  variant="secondary"
                  label="Edit"
                  icon={<Edit2 size={12} />}
                  height={28}
                  onClick={() => handleEdit(trigger)}
                />
                <DillButton
                  variant="danger"
                  label="Delete"
                  icon={<Trash2 size={12} />}
                  height={28}
                  onClick={() => handleDelete(trigger.id)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <CollectionTriggerFormModal
        open={isFormModalOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        initialData={editingTrigger}
        isLoading={
          loadingList.includes('createCollectionTrigger') ||
          loadingList.includes('updateCollectionTrigger')
        }
        errorMessage={errorMessage}
        sequences={sequences}
        supplierBuyers={supplierBuyers}
        isBuyerAvailable={isBuyerAvailable}
      />

      <EnrollCustomersModal
        open={!!addBuyersModalTrigger}
        onClose={handleCloseAddBuyersModal}
        onSubmit={handleAddBuyersSubmit}
        buyerOptions={buyerOptions}
        unavailableCount={unavailableBuyerCount}
        isSubmitting={isAddingBuyers}
        errorMessage={addBuyersErrorMessage}
        title="Add customers"
        subtitle="Picked customers are watched for this trigger's condition - they're enrolled in its sequence as soon as it's already true, or whenever it next becomes true."
        submitLabel="Add"
      />
    </div>
  );
};

export default CollectionTriggersTab;
